import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, ExternalLink, Share2 } from "lucide-react";
import { toast } from "sonner";

interface ShareLinkCardProps {
  token: string;
  expiresAt: string;
  lastAccessedAt: string | null;
  patientName: string;
}

export function ShareLinkCard({ token, expiresAt, lastAccessedAt, patientName }: ShareLinkCardProps) {
  const [copied, setCopied] = useState(false);
  
  const publicUrl = `${window.location.origin}/refeicao/${token}`;
  
  const whatsappMessage = encodeURIComponent(
    `Olá ${patientName}! 👋\n\n` +
    `Aqui está o link para você registrar suas refeições:\n` +
    `${publicUrl}\n\n` +
    `📷 Tire fotos das suas refeições nos horários estabelecidos\n` +
    `🤖 A IA calculará automaticamente as calorias\n\n` +
    `Link válido até ${new Date(expiresAt).toLocaleDateString("pt-BR")}`
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Link copiado para a área de transferência!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Erro ao copiar link");
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${whatsappMessage}`, "_blank");
  };

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Share2 className="h-5 w-5 text-primary" />
          <CardTitle>Link de Acompanhamento</CardTitle>
        </div>
        <CardDescription>
          Compartilhe este link com o paciente para registrar as refeições
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input 
            value={publicUrl} 
            readOnly 
            className="font-mono text-sm"
          />
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleCopy}
            className={copied ? "bg-green-500 text-white" : ""}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex gap-2">
          <Button 
            variant="default" 
            className="flex-1 gap-2 bg-green-600 hover:bg-green-700"
            onClick={handleWhatsApp}
          >
            <Share2 className="h-4 w-4" />
            Enviar via WhatsApp
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => window.open(publicUrl, "_blank")}
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>

        <div className="pt-2 border-t text-sm space-y-1">
          <div className="flex justify-between text-muted-foreground">
            <span>Válido até:</span>
            <span className="font-medium">{new Date(expiresAt).toLocaleDateString("pt-BR")}</span>
          </div>
          {lastAccessedAt && (
            <div className="flex justify-between text-muted-foreground">
              <span>Último acesso:</span>
              <span className="font-medium">
                {new Date(lastAccessedAt).toLocaleString("pt-BR")}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
